<template>
  <!--
    A VITRINE DE PARALLELIUMS, em papel (D15) e com o preço DO SERVIDOR. A versão antiga tinha os
    quatro pacotes cravados no cliente — nome, quantidade e preço — e o preço divergia do que a
    Stripe ia cobrar (o cliente dizia R$ 5,00 onde o servidor cobra R$ 4,99). Agora a lista vem de
    `GET /payments/packages`, que é quem monta o checkout; o cliente só formata.
  -->
  <FishModal :aberto="true" :titulo="$t('shopTitle')" @fechar="$emit('close')">
    <p v-if="carregando" class="pacotes__carregando">…</p>

    <ul v-else class="pacotes">
      <li
        v-for="(pacote, indice) in pacotes"
        :key="pacote.id"
        class="pacotes__pacote"
        :class="{ 'pacotes__pacote--popular': indice === 1 }"
      >
        <FishIcon nome="gem" tamanho="grande" />
        <div class="crescer">
          <p class="pacotes__nome">
            {{ pacote.nome }}
            <span v-if="indice === 1" class="pacotes__popular">{{ $t('popularChoice') }}</span>
          </p>
          <p class="pacotes__quanto t-numero">{{ pacote.paralleliums }} {{ $t('currency') }}</p>
        </div>
        <FishButton variante="primario" tamanho="pequeno" som="compra" @click="escolherPacote(pacote)">
          {{ precoFormatado(pacote) }}
        </FishButton>
      </li>
    </ul>

    <!-- A forma de pagamento: cartão sai na hora; boleto leva dias — o jogador escolhe sabendo. -->
    <FishModal
      v-if="pacoteEscolhido"
      :aberto="true"
      :titulo="$t('selectPaymentMethod')"
      @fechar="pacoteEscolhido = null"
    >
      <div class="pacotes__metodos">
        <FishButton variante="primario" bloco icone="wallet" som="compra" :carregando="processando" @click="processPayment('card')">
          {{ $t('creditCard') }}
        </FishButton>
        <FishButton bloco icone="file-invoice-dollar" som="clique" :carregando="processando" @click="processPayment('boleto')">
          {{ $t('boleto') }}
        </FishButton>
      </div>
      <template #acoes>
        <FishButton tamanho="pequeno" som="clique" @click="pacoteEscolhido = null">
          {{ $t('cancel') }}
        </FishButton>
      </template>
    </FishModal>
  </FishModal>
</template>

<script>
import { mapState } from 'vuex';
import * as v2 from '@/core/services/api/v2';
import { FishModal, FishButton, FishIcon } from '@/shared/ui';

export default {
  name: 'BuyParalleliums',
  components: { FishModal, FishButton, FishIcon },
  emits: ['close'],
  data() {
    return {
      pacotes: [],
      carregando: true,
      pacoteEscolhido: null,
      processando: false,
    }
  },
  computed: {
    ...mapState({
      playerId: state => state.player.id,
    })
  },
  async created() {
    try {
      // O servidor devolve `{ id, nome, paralleliums, preco (centavos), moeda }` — a mesma tabela
      // que o checkout usa, então o que se vê é o que se paga.
      this.pacotes = await v2.pacotesDePagamento(this.$i18n.locale);
    } catch (error) {
      console.error('Erro ao carregar os pacotes:', error);
    } finally {
      this.carregando = false;
    }
  },
  methods: {
    precoFormatado(pacote) {
      const moeda = (pacote.moeda || 'usd').toUpperCase();
      const regiao = moeda === 'BRL' ? 'pt-BR' : 'en-US';
      return new Intl.NumberFormat(regiao, { style: 'currency', currency: moeda }).format(
        (pacote.preco || 0) / 100,
      );
    },
    escolherPacote(pacote) {
      this.pacoteEscolhido = pacote;
    },
    async processPayment(method) {
      if (this.processando) return;
      this.processando = true;
      try {
        // Quem monta a sessão da Stripe é o V2; daqui só se navega para a URL — e `url` pode vir
        // nula, o que não é um checkout válido.
        const { url } = await v2.checkout(this.playerId, {
          packageId: this.pacoteEscolhido.id,
          locale: this.$i18n.locale,
          paymentMethod: method,
        });

        if (!url) {
          throw new Error('Checkout sem URL de pagamento');
        }
        window.location.href = url;
      } catch (error) {
        console.error('Error processing payment:', error);
        this.pacoteEscolhido = null;
      } finally {
        this.processando = false;
      }
    }
  }
}
</script>

<style scoped>
.pacotes__carregando {
  margin: 0;
  color: var(--tinta-fraca);
}

.pacotes {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--e-3);
}

.pacotes__pacote {
  display: flex;
  align-items: center;
  gap: var(--e-3);
  padding-bottom: var(--e-3);
  border-bottom: 1px dashed var(--papel-sombra);
}

.pacotes__pacote:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.pacotes__nome {
  margin: 0;
  font-family: var(--fonte-display);
  font-size: var(--f-2);
  color: var(--tinta);
  display: flex;
  align-items: baseline;
  gap: var(--e-2);
  flex-wrap: wrap;
}

/* O "mais escolhido" é uma anotação a latão, não um selo pulsando (D0). */
.pacotes__popular {
  font-size: var(--f-1);
  color: var(--latao);
}

.pacotes__quanto {
  margin: 0;
  font-size: var(--f-1);
  color: var(--tinta-fraca);
}

.pacotes__metodos {
  display: flex;
  flex-direction: column;
  gap: var(--e-2);
}
</style>
